import axios from 'axios';
import { getValidAdminToken } from '../auth/authManager';
import { getEnvironmentConfig } from '../config/apiConfig';
import { getSelectedEndpointUrl } from '../endpoints/endpointStore';

// Helper to get admin API key safely
const getAdminApiKey = () => {
  const config = getEnvironmentConfig();
  return config?.apiConfig?.ADMIN_API_KEY || '';
};

// Helper to get config service base URL from cached endpoints
const getConfigServiceUrl = async () => {
  try {
    // Get the cached endpoint from localStorage (fetched during login)
    const cachedEndpoint = getSelectedEndpointUrl();

    if (cachedEndpoint) {
      console.log('[InstrumentAPI] Using cached config service endpoint:', cachedEndpoint);
      return cachedEndpoint;
    }

    throw new Error('No config service endpoint cached. Please ensure login completed successfully.');
  } catch (error) {
    console.error('[InstrumentAPI] Failed to get config service URL from cache:', error);
    throw error;
  }
};

// Helper to make requests to config service using /admin-api/v1 prefix
const configServiceRequest = async (url, options = {}) => {
  const token = getValidAdminToken();
  
  if (!token) {
    throw new Error('No admin token found. Please authenticate first.');
  }
  
  const configUrl = await getConfigServiceUrl();
  const apiUrl = url.startsWith('/') ? url : `/${url}`;
  // Ensure protocol is included (https://)
  const baseUrl = configUrl.startsWith('http') ? configUrl : `https://${configUrl}`;
  const fullUrl = `${baseUrl}/admin-api/v1${apiUrl}`;
  
  console.log('[InstrumentAPI] Config service request:', {
    url: apiUrl,
    fullUrl,
    method: options.method || 'GET',
    token: token ? `${token.substring(0, 20)}...` : 'missing',
    apiKey: getAdminApiKey() ? 'present' : 'missing'
  });
  
  try {
    const response = await axios.request({
      url: fullUrl,
      method: options.method || 'GET',
      data: options.data,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'x-api-key': getAdminApiKey(),
        ...options.headers
      },
      timeout: 10000,
      ...options
    });
    
    return response;
  } catch (error) {
    console.error('[InstrumentAPI] Config service request failed:', {
      url,
      fullUrl,
      status: error.response?.status,
      message: error.response?.data?.message || error.message
    });
    throw error;
  }
};

export const instrumentAPI = {
  getAll: async () => {
    try {
      console.log('Attempting to fetch instruments...');
      const response = await configServiceRequest('/instruments', {
        timeout: 10000
      });
      console.log('Instruments API response:', response.data);
      return response;
    } catch (error) {
      console.error('Error fetching instruments:', error);
      
      // Handle CORS errors specifically
      if (error.message.includes('CORS') || error.message.includes('Access to XMLHttpRequest')) {
        console.error('CORS error detected - this is a development issue');
        throw new Error('CORS Error: The server needs to be configured to allow requests from localhost:3000. This is a development environment issue.');
      }
      
      // Handle 401 errors without automatic logout
      if (error.response?.status === 401) {
        throw new Error('Unauthorized: Please check your authentication or re-login if needed.');
      }
      
      throw error;
    }
  },

  add: async (data) => {
    try {
      const response = await configServiceRequest('/instruments', {
        method: 'POST',
        data,
        timeout: 10000
      });
      return response;
    } catch (error) {
      console.error('Error adding instrument:', error);
      
      if (error.message.includes('CORS') || error.message.includes('Access to XMLHttpRequest')) {
        throw new Error('CORS Error: Cannot add instrument due to CORS policy. Server configuration needed.');
      }
      
      throw error;
    }
  },

  create: async (data) => {
    try {
      const response = await configServiceRequest('/instruments', {
        method: 'POST',
        data,
        timeout: 10000
      });
      return response;
    } catch (error) {
      console.error('Error creating instrument:', error);
      
      if (error.message.includes('CORS') || error.message.includes('Access to XMLHttpRequest')) {
        throw new Error('CORS Error: Cannot create instrument due to CORS policy. Server configuration needed.');
      }
      
      throw error;
    }
  },

  update: async (symbol, data) => {
    try {
      console.log('Updating instrument:', { symbol, data });
      const response = await configServiceRequest(`/instruments/${symbol}`, {
        method: 'PUT',
        data,
        timeout: 10000
      });
      console.log('Update successful:', response.data);
      return response;
    } catch (error) {
      console.error('Error updating instrument:', error);
      
      if (error.message.includes('CORS') || error.message.includes('Access to XMLHttpRequest')) {
        throw new Error('CORS Error: Cannot update instrument due to CORS policy. Server configuration needed.');
      }
      
      throw error;
    }
  },

  delete: async (symbol) => {
    try {
      const response = await configServiceRequest(`/instruments/${symbol}`, {
        method: 'DELETE',
        timeout: 10000
      });
      return response;
    } catch (error) {
      console.error('Error deleting instrument:', error);
      
      if (error.message.includes('CORS') || error.message.includes('Access to XMLHttpRequest')) {
        throw new Error('CORS Error: Cannot delete instrument due to CORS policy. Server configuration needed.');
      }
      
      throw error;
    }
  },

  // Trading Hours Management
  getTradingHours: async (symbol) => {
    try {
      console.log(`Fetching trading hours for ${symbol}...`);
      const response = await configServiceRequest(`/instruments/${symbol}/trading-hours`, {
        timeout: 10000
      });
      console.log('Trading hours API response:', response.data);
      return response;
    } catch (error) {
      console.error('Error fetching trading hours:', error);
      
      if (error.message.includes('CORS') || error.message.includes('Access to XMLHttpRequest')) {
        throw new Error('CORS Error: Cannot fetch trading hours due to CORS policy. Server configuration needed.');
      }
      
      throw error;
    }
  },

  updateTradingHours: async (symbol, tradingHoursData) => {
    try {
      console.log(`Updating trading hours for ${symbol}...`, tradingHoursData);
      const response = await configServiceRequest(`/instruments/${symbol}/trading-hours`, {
        method: 'PUT',
        data: tradingHoursData,
        timeout: 10000
      });
      return response;
    } catch (error) {
      console.error('Error updating trading hours:', error);
      
      if (error.message.includes('CORS') || error.message.includes('Access to XMLHttpRequest')) {
        throw new Error('CORS Error: Cannot update trading hours due to CORS policy. Server configuration needed.');
      }
      
      throw error;
    }
  },

  // Market Status
  getMarketStatus: async (symbol) => {
    try {
      console.log(`Fetching market status for ${symbol}...`);
      const response = await configServiceRequest(`/instruments/${symbol}/market-status`, {
        timeout: 10000
      });
      console.log('Market status API response:', response.data);
      return response;
    } catch (error) {
      console.error('Error fetching market status:', error);
      
      if (error.message.includes('CORS') || error.message.includes('Access to XMLHttpRequest')) {
        throw new Error('CORS Error: Cannot fetch market status due to CORS policy. Server configuration needed.');
      }
      
      throw error;
    }
  },

  getAllMarketStatus: async () => {
    try {
      console.log('Fetching market status for all instruments...');
      const response = await configServiceRequest('/instruments/market-status/all', {
        timeout: 10000
      });
      console.log('All market status API response:', response.data);
      return response;
    } catch (error) {
      console.error('Error fetching all market status:', error);
      
      if (error.message.includes('CORS') || error.message.includes('Access to XMLHttpRequest')) {
        throw new Error('CORS Error: Cannot fetch market status due to CORS policy. Server configuration needed.');
      }
      
      throw error;
    }
  },

  // Trading Hours Presets
  getTradingHoursPresets: async (category = null) => {
    try {
      console.log('Fetching trading hours presets...');
      const url = category ? `/instruments/trading-hours/presets?category=${category}` : '/instruments/trading-hours/presets';
      const response = await configServiceRequest(url, {
        timeout: 10000
      });
      console.log('Trading hours presets API response:', response.data);
      return response;
    } catch (error) {
      console.error('Error fetching trading hours presets:', error);
      
      if (error.message.includes('CORS') || error.message.includes('Access to XMLHttpRequest')) {
        throw new Error('CORS Error: Cannot fetch trading hours presets due to CORS policy. Server configuration needed.');
      }
      
      throw error;
    }
  },

  getTradingHoursPreset: async (presetName) => {
    try {
      console.log(`Fetching trading hours preset: ${presetName}...`);
      const response = await configServiceRequest(`/instruments/trading-hours/presets/${encodeURIComponent(presetName)}`, {
        timeout: 10000
      });
      console.log('Trading hours preset API response:', response.data);
      return response;
    } catch (error) {
      console.error('Error fetching trading hours preset:', error);
      
      if (error.message.includes('CORS') || error.message.includes('Access to XMLHttpRequest')) {
        throw new Error('CORS Error: Cannot fetch trading hours preset due to CORS policy. Server configuration needed.');
      }
      
      throw error;
    }
  },

  createTradingHoursPreset: async (presetData) => {
    try {
      console.log('Creating trading hours preset...', presetData);
      const response = await configServiceRequest('/instruments/trading-hours/presets', {
        method: 'POST',
        data: presetData,
        timeout: 10000
      });
      return response;
    } catch (error) {
      console.error('Error creating trading hours preset:', error);
      
      if (error.message.includes('CORS') || error.message.includes('Access to XMLHttpRequest')) {
        throw new Error('CORS Error: Cannot create trading hours preset due to CORS policy. Server configuration needed.');
      }
      
      throw error;
    }
  },

  updateTradingHoursPreset: async (presetName, presetData) => {
    try {
      console.log(`Updating trading hours preset: ${presetName}...`, presetData);
      const response = await configServiceRequest(`/instruments/trading-hours/presets/${encodeURIComponent(presetName)}`, {
        method: 'PUT',
        data: presetData,
        timeout: 10000
      });
      return response;
    } catch (error) {
      console.error('Error updating trading hours preset:', error);
      
      if (error.message.includes('CORS') || error.message.includes('Access to XMLHttpRequest')) {
        throw new Error('CORS Error: Cannot update trading hours preset due to CORS policy. Server configuration needed.');
      }
      
      throw error;
    }
  },

  deleteTradingHoursPreset: async (presetName) => {
    try {
      console.log(`Deleting trading hours preset: ${presetName}...`);
      const response = await configServiceRequest(`/instruments/trading-hours/presets/${encodeURIComponent(presetName)}`, {
        method: 'DELETE',
        timeout: 10000
      });
      return response;
    } catch (error) {
      console.error('Error deleting trading hours preset:', error);
      
      if (error.message.includes('CORS') || error.message.includes('Access to XMLHttpRequest')) {
        throw new Error('CORS Error: Cannot delete trading hours preset due to CORS policy. Server configuration needed.');
      }
      
      throw error;
    }
  },

  applyPresetToInstrument: async (symbol, presetName, options = {}) => {
    try {
      console.log(`Applying preset ${presetName} to instrument ${symbol}...`, options);
      const response = await configServiceRequest(`/instruments/${symbol}/apply-preset/${encodeURIComponent(presetName)}`, {
        method: 'POST',
        data: options,
        timeout: 10000
      });
      return response;
    } catch (error) {
      console.error('Error applying preset to instrument:', error);
      
      if (error.message.includes('CORS') || error.message.includes('Access to XMLHttpRequest')) {
        throw new Error('CORS Error: Cannot apply preset to instrument due to CORS policy. Server configuration needed.');
      }
      
      throw error;
    }
  },

  applyPresetToMultipleInstruments: async (symbols, presetName, options = {}) => {
    try {
      console.log(`Applying preset ${presetName} to multiple instruments...`, { symbols, options });
      const response = await configServiceRequest(`/instruments/bulk/apply-preset/${encodeURIComponent(presetName)}`, {
        method: 'POST',
        data: {
          symbols,
          ...options
        },
        timeout: 30000 // Longer timeout for bulk operations
      });
      return response;
    } catch (error) {
      console.error('Error applying preset to multiple instruments:', error);
      
      if (error.message.includes('CORS') || error.message.includes('Access to XMLHttpRequest')) {
        throw new Error('CORS Error: Cannot apply preset to instruments due to CORS policy. Server configuration needed.');
      }
      
      throw error;
    }
  },

  getPresetCategories: async () => {
    try {
      console.log('Fetching preset categories...');
      const response = await configServiceRequest('/instruments/trading-hours/presets-categories', {
        timeout: 10000
      });
      console.log('Preset categories API response:', response.data);
      return response;
    } catch (error) {
      console.error('Error fetching preset categories:', error);
      
      if (error.message.includes('CORS') || error.message.includes('Access to XMLHttpRequest')) {
        throw new Error('CORS Error: Cannot fetch preset categories due to CORS policy. Server configuration needed.');
      }
      
      throw error;
    }
  }
};
